import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";

interface RecentClaim {
  claimId: string;
  deviceId: string;
  serial: string;
  model: string;
  claimedByEmail: string | null;
  claimSource: string;
  claimedAt: string;
  releasedAt: string | null;
}

function relative(dateStr: string | null): string {
  if (!dateStr) return "-";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function duration(from: string, to: string | null): string {
  const start = new Date(from).getTime();
  const end = to ? new Date(to).getTime() : Date.now();
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  return `${hours}h`;
}

export default function SettingsActivity() {
  const { data, loading, refetch } = useApi<{ claims: RecentClaim[] }>(
    "/api/cli-tokens/recent-claims"
  );
  useSSERefetch("/api/events", ["device:claimed", "device:released"], refetch);

  const claims = data?.claims ?? [];

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)"
      }}
    >
      <h2
        style={{
          fontSize: "var(--font-size-md)",
          fontWeight: 600,
          marginBottom: "var(--space-sm)"
        }}
      >
        Recent Claim Activity (last 7 days)
      </h2>
      <p
        style={{
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-sm)",
          marginBottom: "var(--space-md)"
        }}
      >
        All device claims across CI pipelines and CLI users.
      </p>

      {loading ? (
        <div style={{ color: "var(--color-text-secondary)" }}>Loading...</div>
      ) : claims.length === 0 ? (
        <div style={{ color: "var(--color-text-secondary)" }}>No claims yet.</div>
      ) : (
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Device</th>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Source</th>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Who</th>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Claimed</th>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>Duration</th>
              <th style={{ textAlign: "left", padding: "8px 8px", color: "var(--color-text-secondary)", fontWeight: 500 }}>State</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => (
              <tr
                key={c.claimId}
                style={{ borderBottom: "1px solid var(--color-border)" }}
              >
                <td style={{ padding: "8px 8px" }}>
                  <div style={{ fontWeight: 500 }}>{c.model}</div>
                  <code style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                    {c.serial}
                  </code>
                </td>
                <td style={{ padding: "8px 8px" }}>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "var(--radius-pill)",
                      fontSize: 11,
                      fontWeight: 600,
                      background:
                        c.claimSource === "cli"
                          ? "var(--color-status-available-bg)"
                          : "var(--color-status-busy-bg)",
                      color:
                        c.claimSource === "cli"
                          ? "var(--color-status-available-text)"
                          : "var(--color-status-busy-text)"
                    }}
                  >
                    {c.claimSource}
                  </span>
                </td>
                <td style={{ padding: "8px 8px" }}>
                  {c.claimedByEmail ?? (
                    <span style={{ color: "var(--color-text-secondary)" }}>
                      (pipeline)
                    </span>
                  )}
                </td>
                <td style={{ padding: "8px 8px", color: "var(--color-text-secondary)" }}>
                  {relative(c.claimedAt)}
                </td>
                <td style={{ padding: "8px 8px" }}>
                  {duration(c.claimedAt, c.releasedAt)}
                </td>
                <td style={{ padding: "8px 8px" }}>
                  {c.releasedAt ? (
                    <span style={{ color: "var(--color-text-secondary)" }}>
                      released
                    </span>
                  ) : (
                    <span style={{ color: "var(--color-warning)", fontWeight: 600 }}>
                      active
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
